/* Common Imports */

import makeStyles from "@material-ui/core/styles/makeStyles";
import React from "react";

/* Component Imports */

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Snackbar,
  TextField,
  Typography,
} from "@material-ui/core";
import MuiIconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import MuiAlert from "@material-ui/lab/Alert";
import AppConfig from "../../AppConfig";
import sendToGoogleSheets from "../../lib/sendToGoogleSheets";

/* Data Imports */

const useStyles = makeStyles((theme) => ({
  modal: {
    padding: "1rem",
    width: "100%",
    "& .MuiDialog-paper": {
      borderRadius: "1rem",
      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
  },
  heading: {
    color: "#693471",
    fontWeight: "700",
    fontSize: "1.5rem",
    lineHeight: "2.25rem",
    margin: "0rem 0rem 0.5rem 0rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
    },
  },
  textField: {
    width: "100%",
    margin: "0rem 0rem 2rem 0rem",
    [theme.breakpoints.down("sm")]: {
      margin: "0rem 0rem 1rem 0rem",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderRadius: "0.5rem",
      },
    },
  },
  checkboxRow: {
    marginLeft: "0.5rem",
    paddingBottom: "0rem",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    [theme.breakpoints.up("lg")]: {
      alignItems: "center",
    },
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  grey: {
    fontSize: "0.9rem",
    fontColor: "#202020",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
    },
  },
  link: {
    fontSize: "0.9rem",
    color: "#693471",
    textDecoration: "none",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
    },
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "100%",
    padding: "2rem 1.5rem 2rem 1.5rem",
  },
  dialogActions: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "100%",
    padding: "1rem",
    margin: "0.5rem 0rem 0.5rem 0rem",
    gap: "1rem",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column-reverse",
      justifyContent: "center",
    },
  },
  primaryButton: {
    padding: "0.5rem 1rem",
    background: "-webkit-linear-gradient(180deg, #502761 0%, #74378E 100%)",
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: "500",
    borderRadius: "0.75rem",
    textTransform: "none",
    "&:hover": {
      background: "-webkit-linear-gradient(180deg, #74378E 0%, #502761 100%)",
      color: "#FFFFFF",
      textDecoration: "none",
      boxShadow: "none",
    },
    "&:disabled": {
      background: "#ececec",
      color: "#88888a",
      border: "solid 1px #ececec",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      margin: "0rem 0rem 0rem 0rem",
    },
  },
  cancelButton: {
    background: "#FFFFFF",
    color: "#693471",
    border: "solid 1px #693471",
    padding: "0.5rem 1rem",
    fontSize: "1rem",
    fontWeight: "500",
    textTransform: "none",
    borderRadius: "0.75rem",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
}));

function Alert(props) {
  return (
    <MuiAlert
      elevation={6}
      variant="filled"
      {...props}
    />
  );
}

const EdenBrochureDownloadModal = (props) => {
  const classes = useStyles();

  const [loadingSnackbar, setLoadingSnackbar] = React.useState(false);
  const [successSnackbar, setSuccessSnackbar] = React.useState(false);
  const [errorSnackbar, setErrorSnackbar] = React.useState(false);

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [atc, setAtc] = React.useState(false);
  const [btnDisabled, setBtnDisabled] = React.useState(true);

  React.useEffect(() => {
    if (
      name !== (undefined || "") &&
      phone !== (undefined || "") &&
      phone.match(/^[6-9]\d{9}$/) &&
      email !== (undefined || "") &&
      email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) &&
      atc === true
    ) {
      setBtnDisabled(false);
    } else {
      setBtnDisabled(true);
    }
  }, [name, phone, email, atc]);

  /* Submit Form */
  const submitForm = () => {
    const reqURL = encodeURI(
      AppConfig.apiUrlPrefix +
        "store-download-lead?name=" +
        name +
        "&phone=" +
        phone +
        "&email=" +
        email +
        "&agree=" +
        atc +
        "&source=" +
        (props.source ? props.source : "unknown"),
    );

    console.log(reqURL, "Request URL");
    setBtnDisabled(true);
    setLoadingSnackbar(true);
    fetch(reqURL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log(data);
        if (data.status === "success") {
          setLoadingSnackbar(false);
          setSuccessSnackbar(true);
          downloadPDF();

          // Send to Google Sheets
          sendToGoogleSheets({
            name: name,
            email: email,
            countryCode: "",
            phone: phone,
            source: props.source || "Eden Brochure Download",
            type: "Download Brochure",
          })
            .then((result) => {
              console.log("Google Sheets result:", result);
            })
            .catch((err) => {
              console.error("Google Sheets error:", err);
            });

          // Reset form
          setTimeout(() => {
            setName("");
            setEmail("");
            setPhone("");
            setAtc(false);
            props.onClose();
          }, 2000);
        } else {
          setLoadingSnackbar(false);
          setErrorSnackbar(true);
          setBtnDisabled(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoadingSnackbar(false);
        setErrorSnackbar(true);
        setBtnDisabled(false);
      });
  };

  const handleLoadingSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setLoadingSnackbar(false);
  };

  const handleSuccessSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccessSnackbar(false);
  };

  const handleErrorSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setErrorSnackbar(false);
  };

  const downloadPDF = () => {
    // You can customize the PDF URL here
    const pdfURL = props.pdfURL || "/pdf/heavie-eden-brochure.pdf";
    const link = document.createElement("a");
    link.href = pdfURL;
    link.download = pdfURL.slice(pdfURL.lastIndexOf("/") + 1);
    link.dispatchEvent(new MouseEvent("click"));
  };

  return (
    <React.Fragment>
      <Snackbar
        open={loadingSnackbar}
        autoHideDuration={3000}
        onClose={handleLoadingSnackbarClose}
      >
        <Alert
          onClose={handleLoadingSnackbarClose}
          severity="info"
        >
          Sending information...
        </Alert>
      </Snackbar>
      <Snackbar
        open={successSnackbar}
        autoHideDuration={3000}
        onClose={handleSuccessSnackbarClose}
      >
        <Alert
          onClose={handleSuccessSnackbarClose}
          severity="success"
        >
          Thanks, {name}! Your download will start shortly.
        </Alert>
      </Snackbar>
      <Snackbar
        open={errorSnackbar}
        autoHideDuration={3000}
        onClose={handleErrorSnackbarClose}
      >
        <Alert
          onClose={handleErrorSnackbarClose}
          severity="error"
        >
          Oops! Something seems to have gone wrong!
        </Alert>
      </Snackbar>

      <Dialog
        className={classes.modal}
        onClose={() => {
          setName("");
          setEmail("");
          setPhone("");
          setAtc(false);
          props.onClose();
        }}
        aria-labelledby="contact-download-modal"
        open={props.open}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle
          id="contact-download-modal-title"
          onClose={props.onClose}
        >
          <Typography
            variant="h5"
            component="p"
            className={classes.heading}
          >
            {props.title || "Contact Us"}
          </Typography>
          <MuiIconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={props.onClose}
          >
            <CloseIcon />
          </MuiIconButton>
        </DialogTitle>
        <form
          method="GET"
          id="contact-form"
        >
          <DialogContent
            dividers
            className={classes.dialogContent}
          >
            {/* Name */}
            <TextField
              id="name"
              name="name"
              label="Full Name"
              required={true}
              type="text"
              className={classes.textField}
              onChange={(event) => {
                const value = event.target.value.replace(/[^A-Za-z\s.]/g, "");
                setName(value);
              }}
              value={name}
              variant="outlined"
              inputProps={{
                maxLength: 50,
                pattern: "[A-Za-z\\s.]*",
              }}
            />

            {/* Email */}
            <TextField
              id="email"
              name="email"
              label="Email Address"
              required={true}
              type="email"
              className={classes.textField}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              value={email}
              variant="outlined"
              error={email !== "" && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
              helperText={
                email !== "" && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) ? "Please enter a valid email address" : ""
              }
            />

            {/* Phone */}
            <TextField
              id="phone"
              name="phone"
              label="Phone Number (10 digit)"
              required={true}
              type="tel"
              className={classes.textField}
              onChange={(event) => {
                setPhone(event.target.value);
              }}
              value={phone}
              variant="outlined"
              error={phone !== "" && !phone.match(/^[6-9]\d{9}$/)}
              helperText={
                phone !== "" && !phone.match(/^[6-9]\d{9}$/) ? "Please enter a valid 10-digit mobile number" : ""
              }
              InputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />

            {/* Agree to T&C */}
            <div className={classes.checkboxRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    style={{ padding: "0rem 0.5rem 0rem 0rem" }}
                    checked={atc}
                    onChange={(event) => {
                      setAtc(!atc);
                    }}
                    color="primary"
                    value={atc}
                    name="agree"
                  />
                }
                label={
                  <span>
                    <span className={classes.grey}>I have read and agree with the </span>
                    <Link
                      href="/terms"
                      target="_blank"
                      aria-label="Terms of Service"
                      title="Terms of Service"
                      rel="noopener"
                      className={classes.link}
                    >
                      Terms of Service{" "}
                    </Link>
                    and{" "}
                    <Link
                      href="/privacy"
                      target="_blank"
                      aria-label="Privacy Policy"
                      title="Privacy Policy"
                      rel="noopener"
                      className={classes.link}
                    >
                      Privacy Policy
                    </Link>
                  </span>
                }
              />
            </div>
          </DialogContent>

          <DialogActions
            className={classes.dialogActions}
            disableSpacing
          >
            <Button
              variant="contained"
              label="Close"
              onClick={props.onClose}
              className={classes.cancelButton}
            >
              Close
            </Button>
            <Button
              variant="contained"
              title="Submit"
              onClick={submitForm}
              disabled={btnDisabled}
              rel="noopener"
              className={classes.primaryButton}
            >
              Submit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
};

export default EdenBrochureDownloadModal;
